import { Injectable } from "@angular/core";
import { UiStateService } from "./ui-state-service";
import { NeutUtils, VorsponningSize } from "src/app/shared/models/neut.model";
import { DorpelUtils } from "src/app/shared/models/dorpel-type.model";
import {
  SluitpotUtils,
  SluitpotModel,
} from "src/app/shared/models/sluitpot.model";
import {
  NeutDto,
  BestellingDorpelDto,
  BestellingNeutDto,
} from "../../shared/api-models/models";
interface Coordinate {
  x: number;
  y: number;
}

type NeutDrawingOptions = {
  isBuitenDorpel: boolean,
  isBinnenDorpel: boolean,
  isSlidingDoor: boolean,
  leftCustomSponningmaat?: number
};

type SponningmaatUpdateTargetPoints = {
  left: number[],
  right: number[]
}

type Dimensions = {
  width: number;
  length: number;
}

const DORPEL_CODES = {
  BINNEN_DORPEL: "BI",
  BUITEN_DORPEL: "BU",
  HEFSCHUIF_DORPEL: "HS",
};
const VOORSPONNING_TYPES = {
  LEFT: "links",
  RIGHT: "rechts",
};
const SCALE_TIP_ARROW = {
  WIDTH: 7,
  HEIGHT: 10,
};
const SCALE_HEIGHT = 20;
const DORPEL_START: Coordinate = {
  x: 0,
  y: 60,
};
const DEFAULT_CANVAS_HEIGHT = 260;
const HS_RAIL_WIDTH = 4;
const NEUT_COLOR = "#f3f3f3";
const INVALID_NEUT_COLOR = "#eb5463";
const SCALE_TEXT_FONT = "13px Arial";
const DOOR_FRAME_WIDTH = 2;
const CONTENT_OFFSET = 1;
const DOOR_ELEMENT_WIDTH = 2;
const TEXT_STROKE_WIDTH = 1;
const VOORSPONNING_HEIGHT = 17;
const WISSELNEUT_SPONNINGMAAT_COORDINATES = {
  W1: { right: [2, 3], left: [] },
  W2: { right: [], left: [0, 1, 8] },
  W3: { right: [2, 3], left: [] },
  W4: { right: [], left: [0, 1, 6] },
  W5: { right: [4, 5], left: [0, 1, 8] },
};
@Injectable({
  providedIn: "root",
})
export class GraphicsService {
  constructor(private uiService: UiStateService) { }

  private canvasContext: CanvasRenderingContext2D = null;
  private canvasSize: number;
  private canvasSelector: string;
  private dorpelData: BestellingDorpelDto;

  public initializeDrawing(canvasId: string, size: number): void {
    this.canvasSize = size;
    this.canvasSelector = canvasId;
    let canvas: HTMLCanvasElement = document.getElementById(this.canvasSelector) as HTMLCanvasElement;

    if (canvas) {
      this.setCanvasSize(canvas, this.canvasSize, DEFAULT_CANVAS_HEIGHT);
      this.clearCanvas();
    }
  }

  public setCanvasContext(canvas: HTMLCanvasElement): void {
    let context = canvas.getContext("2d");
    context.save();
    this.canvasContext = context;
  }

  private clearCanvas(): void {
    let ctx: CanvasRenderingContext2D = this.canvasContext;

    if (ctx) {
      ctx.clearRect(0, 0, ctx.canvas.width, 350);
    }
  }

  /**
   * DORPEL DRAWING FUNCTIONS
   */

  public drawDorpel(
    targetDorpelModel: BestellingDorpelDto,
    canvasElement?: HTMLCanvasElement
  ) {
    this.uiService.turnOffDrawingExample();
    this.dorpelData = targetDorpelModel;
    let dorpelCode = this.dorpelData.dorpelCode;
    let damLevel = DorpelUtils.getDamLevelFromDorpelCode(dorpelCode);
    let canvas: HTMLCanvasElement =
      canvasElement ||
      (document.getElementById(this.canvasSelector) as HTMLCanvasElement);

    if (!canvas) {
      return;
    }
    this.setCanvasSize(canvas, this.canvasSize, DEFAULT_CANVAS_HEIGHT);
    this.clearCanvas();
    this.setCanvasContext(canvas);

    this.drawFrame(dorpelCode, DORPEL_START);

    if (DorpelUtils.isSlidingDoor(dorpelCode)) {
      //is sliding door case
      this.drawSlidingNeut();
    } else {
      this.drawNeuten();
    }

    this.cutVerstekSurplus(DORPEL_START, this.canvasSize, dorpelCode);

    this.drawSluitpotten();

    if (!DorpelUtils.isSlidingDoor(dorpelCode)) {
      //no dam lines for sliding doors
      this.drawDamLine(DORPEL_START, damLevel + CONTENT_OFFSET);
    }

    this.canvasContext.restore();
  }

  private drawFrame(dorpelCode: string, startPoint) {
    let ctx: CanvasRenderingContext2D = this.canvasContext;
    let zaagmaat = this.dorpelData.zaagmaat;
    let dorpelHeight = DorpelUtils.getThicknessFromDorpelCode(dorpelCode);

    if (dorpelCode.indexOf(DORPEL_CODES.BINNEN_DORPEL) !== -1) {
      ctx.beginPath();
      ctx.lineWidth = DOOR_FRAME_WIDTH;
      ctx.rect(
        startPoint.x,
        startPoint.y /**dorpel height */,
        this.canvasSize,
        dorpelHeight + 2 * CONTENT_OFFSET
      );
      ctx.stroke();
    } else if (dorpelCode.indexOf(DORPEL_CODES.BUITEN_DORPEL) !== -1) {
      ctx.beginPath();
      ctx.lineWidth = DOOR_FRAME_WIDTH;
      ctx.rect(
        startPoint.x,
        startPoint.y,
        this.canvasSize,
        dorpelHeight + 2 * CONTENT_OFFSET
      );
      ctx.stroke();
    } else if (DorpelUtils.isSlidingDoor(dorpelCode)) {
      ctx.beginPath();
      ctx.lineWidth = DOOR_FRAME_WIDTH;
      ctx.rect(
        startPoint.x,
        startPoint.y,
        this.canvasSize,
        dorpelHeight
      );
      ctx.stroke();
    }

    ctx.lineWidth = DOOR_ELEMENT_WIDTH;
    this.drawScaleMeasurements(ctx, 0, this.canvasSize, 5, SCALE_HEIGHT, zaagmaat);
  }

  private cutVerstekSurplus(startPoint: any, zaagmaat: number, dorpelCode: string) {
    if (!this.uiService.verstekSponningOptions?.isActive) {
      return;
    }

    let dorpelHeight = DorpelUtils.getThicknessFromDorpelCode(dorpelCode);

    let leftVerstek = this.uiService.verstekSponningOptions.isLeftSide
      ? this.calculateVerstekLengthByAngle(dorpelHeight)
      : 0;
    let rightVerstek = this.uiService.verstekSponningOptions.isRightSide
      ? this.calculateVerstekLengthByAngle(dorpelHeight)
      : 0;
    let leftVerstekOrientation = this.uiService.verstekSponningOptions.leftOrientatie;
    let rightVerstekOrientation = this.uiService.verstekSponningOptions.rightOrientatie;

    let upperLeft = {
      x: leftVerstekOrientation === "naarBuiten" ? startPoint.x + leftVerstek : startPoint.x,
      y: startPoint.y,
    };
    let bottomLeft = {
      x: leftVerstekOrientation === "naarBuiten" ? startPoint.x : startPoint.x + leftVerstek,
      y: startPoint.y + dorpelHeight + 2 * CONTENT_OFFSET,
    };
    let bottomRight = {
      x: rightVerstekOrientation === "naarBuiten" ? startPoint.x + zaagmaat : startPoint.x + zaagmaat - rightVerstek,
      y: startPoint.y + dorpelHeight + 2 * CONTENT_OFFSET,
    };
    let upperRight = {
      x: rightVerstekOrientation === "naarBuiten" ? startPoint.x + zaagmaat - rightVerstek : startPoint.x + zaagmaat,
      y: startPoint.y,
    };

    let ctx: CanvasRenderingContext2D = this.canvasContext;

    if (leftVerstek) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      if (leftVerstekOrientation === "naarBuiten") {
        ctx.moveTo(startPoint.x, startPoint.y - DOOR_FRAME_WIDTH);
        ctx.lineTo(upperLeft.x, upperLeft.y - DOOR_FRAME_WIDTH);
        ctx.lineTo(bottomLeft.x, bottomLeft.y + DOOR_FRAME_WIDTH);
        ctx.lineTo(startPoint.x, startPoint.y - DOOR_FRAME_WIDTH);
      } else if (leftVerstekOrientation === "naarBinnen") {
        ctx.moveTo(startPoint.x, startPoint.y - DOOR_FRAME_WIDTH);
        ctx.lineTo(bottomLeft.x, bottomLeft.y + DOOR_FRAME_WIDTH);
        ctx.lineTo(startPoint.x, bottomLeft.y + DOOR_FRAME_WIDTH);
        ctx.lineTo(startPoint.x, startPoint.y - DOOR_FRAME_WIDTH);
      }
      ctx.closePath();
      ctx.fill();

      ctx.globalCompositeOperation = 'source-over';
      ctx.beginPath();
      ctx.moveTo(upperLeft.x, upperLeft.y);
      ctx.lineTo(bottomLeft.x, bottomLeft.y);
      ctx.stroke();
    }

    if (rightVerstek) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      if (rightVerstekOrientation === "naarBuiten") {
        ctx.moveTo(upperRight.x, upperRight.y - DOOR_FRAME_WIDTH);
        ctx.lineTo(zaagmaat, startPoint.y - DOOR_FRAME_WIDTH);
        ctx.lineTo(zaagmaat, bottomRight.y + DOOR_FRAME_WIDTH);
        ctx.lineTo(upperRight.x, upperRight.y - DOOR_FRAME_WIDTH);
      } else if (rightVerstekOrientation === "naarBinnen") {
        ctx.moveTo(upperRight.x, upperRight.y - DOOR_FRAME_WIDTH);
        ctx.lineTo(bottomRight.x, bottomRight.y + DOOR_FRAME_WIDTH);
        ctx.lineTo(upperRight.x, bottomRight.y + DOOR_FRAME_WIDTH);
        ctx.lineTo(upperRight.x, upperRight.y - DOOR_FRAME_WIDTH);
      }
      ctx.closePath();
      ctx.fill();

      ctx.globalCompositeOperation = 'source-over';
      ctx.beginPath();
      ctx.moveTo(upperRight.x, upperRight.y);
      ctx.lineTo(bottomRight.x, bottomRight.y);
      ctx.stroke();
    }

  }

  private drawDamLine(startPoint, damLevel) {
    let ctx: CanvasRenderingContext2D = this.canvasContext;

    ctx.beginPath();
    ctx.save();
    ctx.setLineDash([5]);
    ctx.lineWidth = DOOR_ELEMENT_WIDTH;
    ctx.moveTo(startPoint.x, startPoint.y + damLevel);
    ctx.lineTo(this.canvasSize, startPoint.y + damLevel);
    ctx.stroke();
    ctx.restore();
  }

  /**
   * NEUTEN DRAWING FUNCTIONS
   */

  private drawSlidingNeut() {
    let currentDorpel = this.dorpelData;
    let linkerNeut = currentDorpel.neuten[0];
    let rechterNeut = currentDorpel.neuten[1];
    let thickness = DorpelUtils.getThicknessFromDorpelCode(
      currentDorpel.dorpelCode
    );

    if (!currentDorpel.schema || !linkerNeut.neutCode || !rechterNeut.neutCode) {
      return;
    }

    let linkerNeutDto = this.uiService.getNeutDtoByCodeAndPositie(
      linkerNeut.neutCode,
      linkerNeut.positie
    );
    let rechterNeutDto = this.uiService.getNeutDtoByCodeAndPositie(
      rechterNeut.neutCode,
      rechterNeut.positie
    );
    let linkerNeutDimensions: Dimensions = {
      width: parseInt(linkerNeutDto.neutbreedte),
      length: parseInt(linkerNeutDto.neutlengte)
    }
    let rechterNeutDimensions: Dimensions = {
      width: parseInt(rechterNeutDto.neutbreedte),
      length: parseInt(rechterNeutDto.neutlengte)
    }

    let zaagmaat = currentDorpel.zaagmaat;
    let deviationOptions = {
      klooster: {
        active: this.uiService.kloosterSponningOptions?.isActive,
        data: this.getKloosterSponningData(),
        isLeft: this.uiService.kloosterSponningOptions.isLeftSide,
        isRight: this.uiService.kloosterSponningOptions.isRightSide,
      },
      renovatie: {
        active: this.uiService.renovatieSponningOptions?.isActive,
        data: this.getRenovatieSponningData(),
        isLeft: this.uiService.renovatieSponningOptions.isLeftSide,
        isRight: this.uiService.renovatieSponningOptions.isRightSide,
      },
    };

    switch (currentDorpel.schema) {
      case "AL":
        this.drawALSchema(
          linkerNeutDimensions,
          rechterNeutDimensions,
          zaagmaat,
          thickness,
          deviationOptions
        );
        break;
      case "AR":
        this.drawARSchema(
          linkerNeutDimensions,
          rechterNeutDimensions,
          zaagmaat,
          thickness,
          deviationOptions
        );
        break;
      case "C+G":
        this.drawCGSchema(
          linkerNeutDimensions,
          rechterNeutDimensions,
          thickness,
          deviationOptions
        );
        break;
      case "D":
        this.drawDSchema(
          linkerNeutDimensions,
          rechterNeutDimensions,
          thickness,
          deviationOptions
        );
        break;
    }
  }

  private getScalableElementRatioForALARSchemas(deurmaatA: number, neutWidth: number, maatA: number, maatZ: number, dorpelHeight: number): number {
    let trueScaleMeasurements = neutWidth;
    let scaledMeasurements = deurmaatA;

    if (maatA) {
      if (maatA >= dorpelHeight) {
        trueScaleMeasurements += dorpelHeight;
        scaledMeasurements += maatA - dorpelHeight
      } else {
        trueScaleMeasurements += maatA;
      }
    }

    if (maatZ) {
      if (maatZ >= dorpelHeight) {
        trueScaleMeasurements += dorpelHeight;
        scaledMeasurements += maatZ - dorpelHeight
      } else {
        trueScaleMeasurements += maatZ;
      }
    }

    let ratio = (this.canvasSize - trueScaleMeasurements) / scaledMeasurements;
    return parseFloat(ratio.toFixed(3));
  }

  private drawALSchema(
    linkerNeutDimensions: Dimensions,
    rechterNeutDimensions: Dimensions,
    zaagmaat: number,
    thickness: number,
    deviationOptions: any
  ) {
    let ctx = this.canvasContext;
    let { maatA, deurmaatA, maatZ, dorpelCode } = this.dorpelData;
    let { a1, a2, b, c, baseline } = this.getSlidingNeutValues(
      thickness,
      linkerNeutDimensions.length,//TODO: check how these can be influenced by also having different rightneut dimension
      dorpelCode
    );
    let dorpelHeight = DorpelUtils.getThicknessFromDorpelCode(dorpelCode);

    let hasLeftRenovatieSponning =
      deviationOptions.renovatie.active && deviationOptions.renovatie.isLeft;
    let hasRightRenovatieSponning =
      deviationOptions.renovatie.active && deviationOptions.renovatie.isRight;
    let hasLeftKloosterSponning =
      deviationOptions.klooster.active && deviationOptions.klooster.isLeft;
    let hasRightKloosterSponning =
      deviationOptions.klooster.active && deviationOptions.klooster.isRight;
    let renovatieSponningData = deviationOptions.renovatie.data;
    let kloosterSponningData = deviationOptions.klooster.data;

    maatA = maatA || 0;
    maatZ = maatZ || 0;

    let linkerNeutWidth = linkerNeutDimensions.width;
    let rechterNeutWidth = rechterNeutDimensions.width;

    //SCALING LOGIC
    //NOTE: DEURMAAT INCLUDES RECHTERNEUT WIDTH
    let maxDeurmaatWidth = zaagmaat - maatA - maatZ - linkerNeutWidth;
    let minDeurmaatWidth = linkerNeutWidth + rechterNeutWidth;
    let scalableElementRatio = this.getScalableElementRatioForALARSchemas(maxDeurmaatWidth, linkerNeutWidth, maatA, maatZ, dorpelHeight);
    let scaledMA = Math.min(maatA, dorpelHeight) + Math.max(maatA - dorpelHeight, 0) * scalableElementRatio;
    let scaledMZ = Math.min(maatZ, dorpelHeight) + Math.max(maatZ - dorpelHeight, 0) * scalableElementRatio;
    let maxSpaceForDeurmaat = this.canvasSize - scaledMA - scaledMZ - linkerNeutWidth;
    let deurmaatRatio = maxSpaceForDeurmaat / maxDeurmaatWidth;
    let deurmaatValue = Math.max(minDeurmaatWidth, deurmaatA * deurmaatRatio);
    //END: SCALING LOGIC

    let p2 = scaledMA;
    let p3 = scaledMA + linkerNeutWidth;

    let p4 = this.canvasSize - scaledMZ - deurmaatValue;
    // let p5 = this.canvasSize - scaledMZ - deurmaatValue + neutWidth;

    let p6 = this.canvasSize - scaledMZ - rechterNeutWidth;
    let p7 = this.canvasSize - scaledMZ;

    ctx.beginPath();

    //Linkerneut tekenen
    ctx.moveTo(
      p2 +
      (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
      baseline
    );
    ctx.lineTo(p3, baseline);
    ctx.lineTo(p3, baseline + a1);
    ctx.lineTo(p3 - b, baseline + a1);
    ctx.lineTo(p3 - b, baseline + a1 + c);
    ctx.lineTo(p3, baseline + a1 + c);
    ctx.lineTo(p3, baseline + linkerNeutDimensions.length);
    ctx.lineTo(p2, baseline + linkerNeutDimensions.length);

    if (hasLeftRenovatieSponning) {
      ctx.lineTo(p2, baseline + renovatieSponningData.renovatieLengthLeft);
      ctx.lineTo(
        p2 +
        (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
        baseline + renovatieSponningData.renovatieLengthLeft
      );
    } else if (hasLeftKloosterSponning) {
      ctx.lineTo(
        p2,
        baseline + kloosterSponningData.kloosterOffsetLeft
      );
      ctx.lineTo(
        p2 + kloosterSponningData.kloosterDepthLeft,
        baseline + kloosterSponningData.kloosterOffsetLeft
      );
      ctx.lineTo(
        p2 + kloosterSponningData.kloosterDepthLeft,
        baseline + kloosterSponningData.kloosterOffsetLeft + kloosterSponningData.kloosterLengthLeft
      );
      ctx.lineTo(
        p2,
        baseline + kloosterSponningData.kloosterOffsetLeft + kloosterSponningData.kloosterLengthLeft
      );
    }
    ctx.lineTo(
      p2 +
      (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
      baseline
    );

    //Middenneut tekenen
    // ctx.moveTo(p4, baseline + a1 + c);
    // // ctx.lineTo(p5, baseline + a1 + c);
    // ctx.lineTo(p5, baseline + neutLength);
    // ctx.lineTo(p4, baseline + neutLength);
    // ctx.lineTo(p4, baseline + a1 + c);

    //Rechterneut tekenen
    ctx.moveTo(p6, baseline);

    if (hasRightRenovatieSponning) {
      //with renovatie
      ctx.lineTo(p7 - renovatieSponningData.renovatieDepthRight, baseline);
      ctx.lineTo(
        p7 - renovatieSponningData.renovatieDepthRight,
        baseline + renovatieSponningData.renovatieLengthRight
      );
      ctx.lineTo(p7, baseline + renovatieSponningData.renovatieLengthRight);
    } else if (hasRightKloosterSponning) {
      //with klooster
      ctx.lineTo(p7, baseline);
      ctx.lineTo(
        p7,
        baseline + kloosterSponningData.kloosterOffsetRight
      );
      ctx.lineTo(
        p7 - kloosterSponningData.kloosterDepthRight,
        baseline + kloosterSponningData.kloosterOffsetRight
      );
      ctx.lineTo(
        p7 - kloosterSponningData.kloosterDepthRight,
        baseline + kloosterSponningData.kloosterOffsetRight +
        kloosterSponningData.kloosterLengthRight
      );
      ctx.lineTo(
        p7,
        baseline + kloosterSponningData.kloosterOffsetRight +
        kloosterSponningData.kloosterLengthRight
      );
    } else {
      //regular
      ctx.lineTo(p7, baseline);
    }

    ctx.lineTo(p7, baseline + rechterNeutDimensions.length);
    ctx.lineTo(p6, baseline + rechterNeutDimensions.length);
    ctx.lineTo(p6, baseline + rechterNeutDimensions.length - a2);
    ctx.lineTo(p6 + b, baseline + rechterNeutDimensions.length - a2);
    ctx.lineTo(p6 + b, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.lineTo(p6, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.lineTo(p6, baseline);

    //schuifdeur tekenen
    ctx.moveTo(p3 - b, baseline + a1);
    ctx.lineTo(p6, baseline + a1);
    ctx.lineTo(p6, baseline + a1 + c);
    ctx.lineTo(p3 - b, baseline + a1 + c);
    ctx.lineTo(p3 - b, baseline + a1);

    //rail
    let topLineY = baseline + a1 + c / 2 - HS_RAIL_WIDTH / 2;
    let bottomLineY = topLineY + HS_RAIL_WIDTH;
    ctx.moveTo(p3 - b, topLineY);
    ctx.lineTo(p6, topLineY);
    ctx.moveTo(p3 - b, bottomLineY);
    ctx.lineTo(p6, bottomLineY);

    //vaste deur tekenen
    ctx.moveTo(p4, baseline + rechterNeutDimensions.length - a2);
    ctx.lineTo(p6 + b, baseline + rechterNeutDimensions.length - a2);
    ctx.lineTo(p6 + b, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.lineTo(p4, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.lineTo(p4, baseline + rechterNeutDimensions.length - a2);
    ctx.stroke();

    if (maatA) {
      this.drawScaleMeasurements(
        ctx,
        0,
        scaledMA,
        30,
        SCALE_HEIGHT,
        maatA
      );
    }

    this.drawScaleMeasurements(
      ctx,
      scaledMA,
      scaledMA + linkerNeutWidth,
      30,
      SCALE_HEIGHT,
      linkerNeutWidth
    );

    this.drawScaleMeasurements(
      ctx,
      this.canvasSize - scaledMZ - deurmaatValue,
      this.canvasSize - scaledMZ,
      30,
      SCALE_HEIGHT,
      deurmaatA
    );

    if (maatZ) {
      this.drawScaleMeasurements(
        ctx,
        this.canvasSize - scaledMZ,
        this.canvasSize,
        30,
        SCALE_HEIGHT,
        maatZ
      );
    }
  }

  private drawARSchema(
    linkerNeutDimensions: Dimensions,
    rechterNeutDimensions: Dimensions,
    zaagmaat: number,
    thickness: number,
    deviationOptions: any
  ) {
    let ctx = this.canvasContext;
    let { maatA, deurmaatA, maatZ, dorpelCode } = this.dorpelData;
    let { a1, a2, b, c, baseline } = this.getSlidingNeutValues(
      thickness,
      rechterNeutDimensions.length, //TODO: adapt for different heights scenarios?
      dorpelCode
    );
    let dorpelHeight = DorpelUtils.getThicknessFromDorpelCode(dorpelCode)

    let hasLeftRenovatieSponning =
      deviationOptions.renovatie.active && deviationOptions.renovatie.isLeft;
    let hasRightRenovatieSponning =
      deviationOptions.renovatie.active && deviationOptions.renovatie.isRight;
    let hasLeftKloosterSponning =
      deviationOptions.klooster.active && deviationOptions.klooster.isLeft;
    let hasRightKloosterSponning =
      deviationOptions.klooster.active && deviationOptions.klooster.isRight;
    let renovatieSponningData = deviationOptions.renovatie.data;
    let kloosterSponningData = deviationOptions.klooster.data;
    let linkerNeutWidth = linkerNeutDimensions.width;
    let rechterNeutWidth = rechterNeutDimensions.width;

    maatA = maatA || 0;
    maatZ = maatZ || 0;

    //SCALING LOGIC
    let maxDeurmaatWidth = zaagmaat - maatA - maatZ - rechterNeutWidth;
    let minDeurmaatWidth = linkerNeutWidth + rechterNeutWidth;
    let scalableElementRatio = this.getScalableElementRatioForALARSchemas(maxDeurmaatWidth, rechterNeutWidth, maatA, maatZ, dorpelHeight);
    let scaledMA = Math.min(maatA, dorpelHeight) + Math.max(maatA - dorpelHeight, 0) * scalableElementRatio;
    let scaledMZ = Math.min(maatZ, dorpelHeight) + Math.max(maatZ - dorpelHeight, 0) * scalableElementRatio;
    let maxSpaceForDeurmaat = this.canvasSize - scaledMA - scaledMZ - rechterNeutWidth;
    let deurmaatRatio = maxSpaceForDeurmaat / maxDeurmaatWidth;
    let deurmaatValue = Math.max(minDeurmaatWidth, deurmaatA * deurmaatRatio);
    //END: SCALING LOGIC

    let p2 = scaledMA;
    let p3 = scaledMA + linkerNeutWidth;
    // let p4 = scaledMA + deurmaatValue - neutWidth;
    let p5 = scaledMA + deurmaatValue;
    let p6 = this.canvasSize - scaledMZ - rechterNeutWidth;
    let p7 = this.canvasSize - scaledMZ;

    ctx.beginPath();

    //Linkerneut tekenen
    ctx.moveTo(
      p2 +
      (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
      baseline
    );
    ctx.lineTo(p3, baseline);
    ctx.lineTo(p3, baseline + linkerNeutDimensions.length - a2 - c);
    ctx.lineTo(p3 - b, baseline + linkerNeutDimensions.length - a2 - c);
    ctx.lineTo(p3 - b, baseline + linkerNeutDimensions.length - a2);
    ctx.lineTo(p3, baseline + linkerNeutDimensions.length - a2);
    ctx.lineTo(p3, baseline + linkerNeutDimensions.length);
    ctx.lineTo(p2, baseline + linkerNeutDimensions.length);

    if (hasLeftRenovatieSponning) {
      ctx.lineTo(p2, baseline + renovatieSponningData.renovatieLengthLeft);
      ctx.lineTo(
        p2 +
        (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
        baseline + renovatieSponningData.renovatieLengthLeft
      );
    } else if (hasLeftKloosterSponning) {
      ctx.lineTo(
        p2,
        baseline + kloosterSponningData.kloosterOffsetLeft
      );
      ctx.lineTo(
        p2 + kloosterSponningData.kloosterDepthLeft,
        baseline + kloosterSponningData.kloosterOffsetLeft
      );
      ctx.lineTo(
        p2 + kloosterSponningData.kloosterDepthLeft,
        baseline + kloosterSponningData.kloosterOffsetLeft + kloosterSponningData.kloosterLengthLeft
      );
      ctx.lineTo(
        p2,
        baseline + kloosterSponningData.kloosterOffsetLeft + kloosterSponningData.kloosterLengthLeft
      );
    }

    ctx.lineTo(
      p2 +
      (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
      baseline
    );

    // //Middenneut tekenen
    // ctx.moveTo(p4, baseline + a1 + c); // ask jacob what value should be inserted in place of 66
    // ctx.lineTo(p5, baseline + a1 + c);
    // ctx.lineTo(p5, baseline + neutLength);
    // ctx.lineTo(p4, baseline + neutLength);
    // ctx.lineTo(p4, baseline + a1 + c);

    //Rechterneut tekenen
    ctx.moveTo(p6, baseline);
    ctx.lineTo(p6, baseline + a1);
    ctx.lineTo(p6 + b, baseline + a1);
    ctx.lineTo(p6 + b, baseline + a1 + c);
    ctx.lineTo(p6, baseline + a1 + c);
    ctx.lineTo(p6, baseline + rechterNeutDimensions.length);
    ctx.lineTo(p7, baseline + rechterNeutDimensions.length);

    if (hasRightRenovatieSponning) {
      //with renovatie
      ctx.lineTo(p7, baseline + renovatieSponningData.renovatieLengthRight);
      ctx.lineTo(
        p7 - renovatieSponningData.renovatieDepthRight,
        baseline + renovatieSponningData.renovatieLengthRight
      );
      ctx.lineTo(p7 - renovatieSponningData.renovatieDepthRight, baseline);
    } else if (hasRightKloosterSponning) {
      //with klooster
      ctx.lineTo(
        p7,
        baseline + kloosterSponningData.kloosterOffsetRight +
        kloosterSponningData.kloosterLengthRight
      );
      ctx.lineTo(
        p7 - kloosterSponningData.kloosterDepthRight,
        baseline + kloosterSponningData.kloosterOffsetRight +
        kloosterSponningData.kloosterLengthRight
      );
      ctx.lineTo(
        p7 - kloosterSponningData.kloosterDepthRight,
        baseline + kloosterSponningData.kloosterOffsetRight
      );
      ctx.lineTo(
        p7,
        baseline + kloosterSponningData.kloosterOffsetRight
      );
      ctx.lineTo(p7, baseline);
    } else {
      //regular
      ctx.lineTo(p7, baseline);
    }
    ctx.lineTo(p6, baseline);

    // hefschuifdeur
    ctx.moveTo(p3, baseline + a1);
    ctx.lineTo(p6 + b, baseline + a1);
    ctx.lineTo(p6 + b, baseline + a1 + c);
    ctx.lineTo(p3, baseline + a1 + c);
    ctx.lineTo(p3, baseline + a1);

    //rail
    let topLineY = baseline + a1 + c / 2 - HS_RAIL_WIDTH / 2;
    let bottomLineY = topLineY + HS_RAIL_WIDTH;
    ctx.moveTo(p3, topLineY);
    ctx.lineTo(p6 + b, topLineY);
    ctx.moveTo(p3, bottomLineY);
    ctx.lineTo(p6 + b, bottomLineY);

    // vaste deurmaat
    ctx.moveTo(p3 - b, baseline + linkerNeutDimensions.length - a2);
    ctx.lineTo(p5, baseline + linkerNeutDimensions.length - a2);
    ctx.lineTo(p5, baseline + linkerNeutDimensions.length - a2 - c);
    ctx.lineTo(p3 - b, baseline + linkerNeutDimensions.length - a2 - c);
    ctx.lineTo(p3 - b, baseline + linkerNeutDimensions.length - a2);
    ctx.stroke();

    if (maatA) {
      this.drawScaleMeasurements(ctx, 0, scaledMA, 30, SCALE_HEIGHT, maatA);
    }

    this.drawScaleMeasurements(ctx, scaledMA, scaledMA + deurmaatValue, 30, SCALE_HEIGHT, deurmaatA);
    this.drawScaleMeasurements(ctx, this.canvasSize - scaledMZ - rechterNeutWidth, this.canvasSize - scaledMZ, 30, SCALE_HEIGHT, rechterNeutWidth);

    if (maatZ) {
      this.drawScaleMeasurements(ctx, this.canvasSize - scaledMZ, this.canvasSize, 30, SCALE_HEIGHT, maatZ);
    }
  }

  private getScalableElementRatioForCGSchemas(maatA: number, deurmaatA: number, maatB: number, deurmaatC: number, maatZ: number, dorpelHeight: number): number {
    let trueScaleMeasurements = 0;
    let scaledMeasurements = deurmaatA + maatB + deurmaatC;

    if (maatA) {
      if (maatA >= dorpelHeight) {
        trueScaleMeasurements += dorpelHeight;
        scaledMeasurements += maatA - dorpelHeight
      } else {
        trueScaleMeasurements += maatA;
      }
    }

    if (maatZ) {
      if (maatZ >= dorpelHeight) {
        trueScaleMeasurements += dorpelHeight;
        scaledMeasurements += maatZ - dorpelHeight
      } else {
        trueScaleMeasurements += maatZ;
      }
    }

    let ratio = (this.canvasSize - trueScaleMeasurements) / scaledMeasurements;
    return parseFloat(ratio.toFixed(3));
  }

  private drawCGSchema(
    linkerNeutDimensions: Dimensions,
    rechterNeutDimensions: Dimensions,
    thickness: number,
    deviationOptions: any
  ) {
    let ctx = this.canvasContext;
    let { maatA, deurmaatA, maatB, deurmaatC, maatZ, dorpelCode } = this.dorpelData;
    let { a2, b, c, baseline } = this.getSlidingNeutValues(
      thickness,
      linkerNeutDimensions.length,
      dorpelCode
    );
    let dorpelHeight = DorpelUtils.getThicknessFromDorpelCode(dorpelCode);

    let hasLeftRenovatieSponning =
      deviationOptions.renovatie.active && deviationOptions.renovatie.isLeft;
    let hasRightRenovatieSponning =
      deviationOptions.renovatie.active && deviationOptions.renovatie.isRight;
    let hasLeftKloosterSponning =
      deviationOptions.klooster.active && deviationOptions.klooster.isLeft;
    let hasRightKloosterSponning =
      deviationOptions.klooster.active && deviationOptions.klooster.isRight;
    let renovatieSponningData = deviationOptions.renovatie.data;
    let kloosterSponningData = deviationOptions.klooster.data;

    let linkerNeutWidth = linkerNeutDimensions.width;
    let rechterNeutWidth = rechterNeutDimensions.width;

    maatA = maatA || 0;
    maatZ = maatZ || 0;
    maatB = maatB || 0;

    let minDeurmaat = linkerNeutWidth + rechterNeutWidth;
    let ratio = this.getScalableElementRatioForCGSchemas(maatA, deurmaatA, maatB, deurmaatC, maatZ, dorpelHeight);
    let scaledMA = Math.min(maatA, dorpelHeight) + Math.max(maatA - dorpelHeight, 0) * ratio;
    let scaledMZ = Math.min(maatZ, dorpelHeight) + Math.max(maatZ - dorpelHeight, 0) * ratio;

    let deurmaatAVal = Math.max(minDeurmaat, deurmaatA * ratio);
    let deurmaatCVal = Math.max(minDeurmaat, deurmaatC * ratio);
    let maxMaatB = this.canvasSize - scaledMA - scaledMZ - 2 * (linkerNeutWidth + rechterNeutWidth);
    let maatBVal = Math.min(maatB * ratio, maxMaatB);

    let p2 = scaledMA;
    let p3 = scaledMA + linkerNeutWidth;
    // let p4 = scaledMA + deurmaatAVal - neutWidth;
    let p5 = scaledMA + deurmaatAVal;
    let p6 = scaledMA + deurmaatAVal + maatBVal;
    // let p7 = scaledMA + deurmaatAVal + maatBVal + neutWidth;
    let p8 = scaledMA + deurmaatAVal + maatBVal + deurmaatCVal - rechterNeutWidth;
    let p9 = scaledMA + deurmaatAVal + maatBVal + deurmaatCVal;

    ctx.beginPath();

    //Linkerneut tekenen
    ctx.moveTo(
      p2 +
      (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
      baseline
    );
    ctx.lineTo(p3, baseline);
    ctx.lineTo(p3, baseline + linkerNeutDimensions.length - a2 - c);
    ctx.lineTo(p3 - b, baseline + linkerNeutDimensions.length - a2 - c);
    ctx.lineTo(p3 - b, baseline + linkerNeutDimensions.length - a2);
    ctx.lineTo(p3, baseline + linkerNeutDimensions.length - a2);
    ctx.lineTo(p3, baseline + linkerNeutDimensions.length);
    ctx.lineTo(p2, baseline + linkerNeutDimensions.length);

    if (hasLeftRenovatieSponning) {
      ctx.lineTo(p2, baseline + renovatieSponningData.renovatieLengthLeft);
      ctx.lineTo(
        p2 +
        (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
        baseline + renovatieSponningData.renovatieLengthLeft
      );
    } else if (hasLeftKloosterSponning) {
      ctx.lineTo(
        p2,
        baseline + kloosterSponningData.kloosterOffsetLeft
      );
      ctx.lineTo(
        p2 + kloosterSponningData.kloosterDepthLeft,
        baseline + kloosterSponningData.kloosterOffsetLeft
      );
      ctx.lineTo(
        p2 + kloosterSponningData.kloosterDepthLeft,
        baseline + kloosterSponningData.kloosterOffsetLeft + kloosterSponningData.kloosterLengthLeft
      );
      ctx.lineTo(
        p2,
        baseline + kloosterSponningData.kloosterOffsetLeft + kloosterSponningData.kloosterLengthLeft
      );
    }

    ctx.lineTo(
      p2 +
      (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
      baseline
    );

    //Middenneut 1 tekenen
    // ctx.moveTo(p4, baseline + a2 + c);
    // ctx.lineTo(p5, baseline + a2 + c);
    // ctx.lineTo(p5, baseline + neutLength);
    // ctx.lineTo(p4, baseline + neutLength);
    // ctx.lineTo(p4, baseline + a2 + c);

    // //Middenneut 2 tekenen
    // ctx.moveTo(p6, baseline + a2 + c);
    // ctx.lineTo(p7, baseline + a2 + c);
    // ctx.lineTo(p7, baseline + neutLength);
    // ctx.lineTo(p6, baseline + neutLength);
    // ctx.lineTo(p6, baseline + a2 + c);

    //Rechterneut tekenen
    ctx.moveTo(p8, baseline);

    if (hasRightRenovatieSponning) {
      //with renovatie
      ctx.lineTo(p9 - renovatieSponningData.renovatieDepthRight, baseline);
      ctx.lineTo(
        p9 - renovatieSponningData.renovatieDepthRight,
        baseline + renovatieSponningData.renovatieLengthRight
      );
      ctx.lineTo(p9, baseline + renovatieSponningData.renovatieLengthRight);
    } else if (hasRightKloosterSponning) {
      //with klooster
      ctx.lineTo(p9, baseline);
      ctx.lineTo(
        p9,
        baseline + kloosterSponningData.kloosterOffsetRight
      );
      ctx.lineTo(
        p9 - kloosterSponningData.kloosterDepthRight,
        baseline + kloosterSponningData.kloosterOffsetRight
      );
      ctx.lineTo(
        p9 - kloosterSponningData.kloosterDepthRight,
        baseline + kloosterSponningData.kloosterOffsetRight +
        kloosterSponningData.kloosterLengthRight
      );
      ctx.lineTo(
        p9,
        baseline + kloosterSponningData.kloosterOffsetRight +
        kloosterSponningData.kloosterLengthRight
      );
    } else {
      //regular
      ctx.lineTo(p9, baseline);
    }

    ctx.lineTo(p9, baseline + rechterNeutDimensions.length);
    ctx.lineTo(p8, baseline + rechterNeutDimensions.length);
    ctx.lineTo(p8, baseline + rechterNeutDimensions.length - a2);
    ctx.lineTo(p8 + b, baseline + rechterNeutDimensions.length - a2);
    ctx.lineTo(p8 + b, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.lineTo(p8, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.lineTo(p8, baseline);

    // hefschuifdeur
    ctx.moveTo(p3, baseline + a2);
    ctx.lineTo(p8, baseline + a2);
    ctx.lineTo(p8, baseline + a2 + c);
    ctx.lineTo(p3, baseline + a2 + c);
    ctx.lineTo(p3, baseline + a2);

    //rail
    let topLineY = baseline + a2 + c / 2 - HS_RAIL_WIDTH / 2;
    let bottomLineY = topLineY + HS_RAIL_WIDTH;
    ctx.moveTo(p3, topLineY);
    ctx.lineTo(p8, topLineY);
    ctx.moveTo(p3, bottomLineY);
    ctx.lineTo(p8, bottomLineY);

    // vaste deurmaat a
    ctx.moveTo(p3 - b, baseline + linkerNeutDimensions.length - a2);
    ctx.lineTo(p5, baseline + linkerNeutDimensions.length - a2);
    ctx.lineTo(p5, baseline + linkerNeutDimensions.length - a2 - c);
    ctx.lineTo(p3 - b, baseline + linkerNeutDimensions.length - a2 - c);
    ctx.lineTo(p3 - b, baseline + linkerNeutDimensions.length - a2);
    ctx.stroke();

    // vaste deurmaat c
    ctx.moveTo(p6, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.lineTo(p8 + b, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.lineTo(p8 + b, baseline + rechterNeutDimensions.length - a2);
    ctx.lineTo(p6, baseline + rechterNeutDimensions.length - a2);
    ctx.lineTo(p6, baseline + rechterNeutDimensions.length - a2 - c);
    ctx.stroke();

    if (maatA) {
      this.drawScaleMeasurements(ctx, 0, scaledMA, 30, SCALE_HEIGHT, maatA);
    }
    this.drawScaleMeasurements(ctx, scaledMA, scaledMA + deurmaatAVal, 30, SCALE_HEIGHT, deurmaatA);
    this.drawScaleMeasurements(
      ctx,
      scaledMA + deurmaatAVal,
      scaledMA + deurmaatAVal + maatBVal,
      30,
      SCALE_HEIGHT,
      maatB,
      true
    );
    this.drawScaleMeasurements(
      ctx,
      scaledMA + deurmaatAVal + maatBVal,
      scaledMA + deurmaatAVal + maatBVal + deurmaatCVal,
      30,
      SCALE_HEIGHT,
      deurmaatC
    );

    if (maatZ) {
      this.drawScaleMeasurements(ctx, this.canvasSize - scaledMZ, this.canvasSize, 30, SCALE_HEIGHT, maatZ);
    }
  }

  private getScalableElementRatioForDSchemas(maatA: number, firstMaatA: number, deurmaatB: number, maatC: number, maatZ: number, leftneutWidth: number, rightNeutWidth: number, dorpelHeight: number): number {
    let totalVariableElementsLength = deurmaatB;
    let totalFixedElementsLength = 0;
    if (firstMaatA === leftneutWidth) {
      totalFixedElementsLength += firstMaatA
    } else {
      totalVariableElementsLength += firstMaatA
    }

    if (maatC === rightNeutWidth) {
      totalFixedElementsLength += maatC
    } else {
      totalVariableElementsLength += maatC;
    }

    if (maatA) {
      if (maatA >= dorpelHeight) {
        totalFixedElementsLength += dorpelHeight;
        totalVariableElementsLength += maatA - dorpelHeight
      } else {
        totalFixedElementsLength += maatA;
      }
    }

    if (maatZ) {
      if (maatZ >= dorpelHeight) {
        totalFixedElementsLength += dorpelHeight;
        totalVariableElementsLength += maatZ - dorpelHeight
      } else {
        totalFixedElementsLength += maatZ;
      }
    }

    let ratio = (this.canvasSize - totalFixedElementsLength) / totalVariableElementsLength;
    return parseFloat(ratio.toFixed(3));
  }

  private drawDSchema(
    linkerNeutDimensions: Dimensions,
    rechterNeutDimensions: Dimensions,
    thickness: number,
    deviationOptions: any
  ) {
    let ctx = this.canvasContext;
    let { maatA, firstMaatA, deurmaatB, maatC, maatZ, dorpelCode } = this.dorpelData;
    let { a1, a2, b, c, baseline } = this.getSlidingNeutValues(
      thickness,
      linkerNeutDimensions.length,
      dorpelCode
    );
    let dorpelHeight = DorpelUtils.getThicknessFromDorpelCode(dorpelCode);

    let hasLeftRenovatieSponning =
      deviationOptions.renovatie.active && deviationOptions.renovatie.isLeft;
    let hasRightRenovatieSponning =
      deviationOptions.renovatie.active && deviationOptions.renovatie.isRight;
    let hasLeftKloosterSponning =
      deviationOptions.klooster.active && deviationOptions.klooster.isLeft;
    let hasRightKloosterSponning =
      deviationOptions.klooster.active && deviationOptions.klooster.isRight;
    let renovatieSponningData = deviationOptions.renovatie.data;
    let kloosterSponningData = deviationOptions.klooster.data;

    maatA = maatA || 0;
    maatZ = maatZ || 0;

    let linkerNeutWidth = linkerNeutDimensions.width;
    let rechterNeutWidth = rechterNeutDimensions.width;

    let ratio = this.getScalableElementRatioForDSchemas(maatA, firstMaatA, deurmaatB, maatC, maatZ, linkerNeutWidth, rechterNeutWidth, dorpelHeight);
    let scaledMA = Math.min(maatA, dorpelHeight) + Math.max(maatA - dorpelHeight, 0) * ratio;
    let scaledMZ = Math.min(maatZ, dorpelHeight) + Math.max(maatZ - dorpelHeight, 0) * ratio;

    //if maat equals the neut witdth (which we draw real scale), we also skip applying the ratio and draw the maat real scale too
    let firstMaatAVal = Math.max(firstMaatA === linkerNeutWidth ? firstMaatA : firstMaatA * ratio, linkerNeutWidth);
    let maatCVal = Math.max(maatC === rechterNeutWidth ? maatC : maatC * ratio, rechterNeutWidth);

    let maxDeurmaatB = this.canvasSize - scaledMA - scaledMZ - firstMaatAVal - maatCVal;
    let minDeurmaatB = linkerNeutWidth + rechterNeutWidth;

    let deurmaatBVal = Math.min(maxDeurmaatB, Math.max(minDeurmaatB, deurmaatB * ratio))


    var p2 = scaledMA;
    var p3 = scaledMA + linkerNeutWidth;
    var p4 = scaledMA + firstMaatAVal;
    // var p5 = scaledMA + firstMaatAVal + neutWidth;
    // var p6 = scaledMA + firstMaatAVal + deurmaatBVal - neutWidth;
    var p7 = scaledMA + firstMaatAVal + deurmaatBVal;
    var p8 = scaledMA + firstMaatAVal + deurmaatBVal + maatCVal - rechterNeutWidth;
    var p9 = scaledMA + firstMaatAVal + deurmaatBVal + maatCVal;

    ctx.beginPath();

    // Linkerneut tekenen
    ctx.moveTo(
      p2 +
      (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
      baseline
    );
    ctx.lineTo(p3, baseline);
    ctx.lineTo(p3, baseline + a1);
    ctx.lineTo(p3 - b, baseline + a1);
    ctx.lineTo(p3 - b, baseline + a1 + c);
    ctx.lineTo(p3, baseline + a1 + c);
    ctx.lineTo(p3, baseline + linkerNeutDimensions.length);
    ctx.lineTo(p2, baseline + linkerNeutDimensions.length);

    if (hasLeftRenovatieSponning) {
      ctx.lineTo(p2, baseline + renovatieSponningData.renovatieLengthLeft);
      ctx.lineTo(
        p2 +
        (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
        baseline + renovatieSponningData.renovatieLengthLeft
      );
    } else if (hasLeftKloosterSponning) {
      ctx.lineTo(
        p2,
        baseline + kloosterSponningData.kloosterOffsetLeft
      );
      ctx.lineTo(
        p2 + kloosterSponningData.kloosterDepthLeft,
        baseline + kloosterSponningData.kloosterOffsetLeft
      );
      ctx.lineTo(
        p2 + kloosterSponningData.kloosterDepthLeft,
        baseline + kloosterSponningData.kloosterOffsetLeft + kloosterSponningData.kloosterLengthLeft
      );
      ctx.lineTo(
        p2,
        baseline + kloosterSponningData.kloosterOffsetLeft + kloosterSponningData.kloosterLengthLeft
      );
    }

    ctx.lineTo(
      p2 +
      (hasLeftRenovatieSponning ? renovatieSponningData.renovatieDepthLeft : 0),
      baseline
    );

    // //Middenneut 1 tekenen
    // ctx.moveTo(p4, baseline + a2 + c);
    // ctx.lineTo(p5, baseline + a2 + c);
    // ctx.lineTo(p5, baseline + neutLength);
    // ctx.lineTo(p4, baseline + neutLength);
    // ctx.lineTo(p4, baseline + a2 + c);

    // //Middenneut 2 tekenen
    // ctx.moveTo(p6, baseline + a2 + c);
    // ctx.lineTo(p7, baseline + a2 + c);
    // ctx.lineTo(p7, baseline + neutLength);
    // ctx.lineTo(p6, baseline + neutLength);
    // ctx.lineTo(p6, baseline + a2 + c);

    //Rechterneut tekenen
    ctx.moveTo(p8, baseline);
    ctx.lineTo(p8, baseline + a1);
    ctx.lineTo(p8 + b, baseline + a1);
    ctx.lineTo(p8 + b, baseline + a1 + c);
    ctx.lineTo(p8, baseline + a1 + c);
    ctx.lineTo(p8, baseline + rechterNeutDimensions.length);
    ctx.lineTo(p9, baseline + rechterNeutDimensions.length);

    if (hasRightRenovatieSponning) {
      //with renovatie
      ctx.lineTo(p9, baseline + renovatieSponningData.renovatieLengthRight);
      ctx.lineTo(
        p9 - renovatieSponningData.renovatieDepthRight,
        baseline + renovatieSponningData.renovatieLengthRight
      );
      ctx.lineTo(p9 - renovatieSponningData.renovatieDepthRight, baseline);
    } else if (hasRightKloosterSponning) {
      //with klooster
      ctx.lineTo(
        p9,
        baseline + kloosterSponningData.kloosterOffsetRight +
        kloosterSponningData.kloosterLengthRight
      );
      ctx.lineTo(
        p9 - kloosterSponningData.kloosterDepthRight,
        baseline + kloosterSponningData.kloosterOffsetRight +
        kloosterSponningData.kloosterLengthRight
      );
      ctx.lineTo(
        p9 - kloosterSponningData.kloosterDepthRight,
        baseline + kloosterSponningData.kloosterOffsetRight
      );
      ctx.lineTo(
        p9,
        baseline + kloosterSponningData.kloosterOffsetRight
      );
      ctx.lineTo(p9, baseline);
    } else {
      //regular
      ctx.lineTo(p9, baseline);
    }
    ctx.lineTo(p8, baseline);

    // // hefschuifdeur
    ctx.moveTo(p3 - b, baseline + a1);
    ctx.lineTo(p8 + b, baseline + a1);
    ctx.lineTo(p8 + b, baseline + a1 + c);
    ctx.lineTo(p3 - b, baseline + a1 + c);
    ctx.lineTo(p3 - b, baseline + a1);

    //rail
    let topLineY = baseline + a1 + c / 2 - HS_RAIL_WIDTH / 2;
    let bottomLineY = topLineY + HS_RAIL_WIDTH;
    ctx.moveTo(p3 - b, topLineY);
    ctx.lineTo(p8 + b, topLineY);
    ctx.moveTo(p3 - b, bottomLineY);
    ctx.lineTo(p8 + b, bottomLineY);

    // vaste deurmaat B
    let neutLength = Math.max(linkerNeutDimensions.length, rechterNeutDimensions.length);
    ctx.moveTo(p4, baseline + neutLength);
    ctx.lineTo(p7, baseline + neutLength);
    ctx.lineTo(p7, baseline + neutLength - c);
    ctx.lineTo(p4, baseline + neutLength - c);
    ctx.lineTo(p4, baseline + neutLength);
    ctx.stroke();

    if (maatA) {
      this.drawScaleMeasurements(
        ctx,
        0,
        scaledMA,
        30,
        SCALE_HEIGHT,
        maatA
      );
    }

    this.drawScaleMeasurements(
      ctx,
      scaledMA,
      scaledMA + firstMaatAVal,
      30,
      SCALE_HEIGHT,
      firstMaatA
    );
    this.drawScaleMeasurements(
      ctx,
      scaledMA + firstMaatAVal,
      scaledMA + firstMaatAVal + deurmaatBVal,
      30,
      SCALE_HEIGHT,
      deurmaatB
    );
    this.drawScaleMeasurements(
      ctx,
      scaledMA + firstMaatAVal + deurmaatBVal,
      this.canvasSize - scaledMZ,
      30,
      SCALE_HEIGHT,
      maatC
    );

    if (maatZ) {
      this.drawScaleMeasurements(
        ctx,
        this.canvasSize - scaledMZ,
        this.canvasSize,
        30,
        SCALE_HEIGHT,
        maatZ
      );
    }
  }

  private calculateDaagmatVirtualRatio(neuten: BestellingNeutDto[], maatA: number, maatZ: number, dorpelHeight: number): number {
    let trueScaleMeasurements = 0;
    let scaledMeasurements = 0;

    if (maatA) {
      if (maatA >= dorpelHeight) {
        trueScaleMeasurements += dorpelHeight;
        scaledMeasurements += maatA - dorpelHeight
      } else {
        trueScaleMeasurements += maatA;
      }
    }

    if (maatZ) {
      if (maatZ >= dorpelHeight) {
        trueScaleMeasurements += dorpelHeight;
        scaledMeasurements += maatZ - dorpelHeight
      } else {
        trueScaleMeasurements += maatZ;
      }
    }

    neuten.forEach(neut => {
      let neutDto: NeutDto = this.uiService.getNeutDtoByCodeAndPositie(
        neut.neutCode,
        neut.positie
      );
      let neutWidth = neut.customWidth || parseInt(neutDto.neutbreedte);
      trueScaleMeasurements += neutWidth;
      scaledMeasurements += neut.daagmaat || 0
    });

    let ratio = (this.canvasSize - trueScaleMeasurements) / scaledMeasurements;
    return parseFloat(ratio.toFixed(3));
  }

  private compensateForRightEndAccuracy(initialNextNeutPosition, nextNeut) {
    const TRESHOLD = 3;
    let neutDto: NeutDto = this.uiService.getNeutDtoByCodeAndPositie(
      nextNeut.neutCode,
      nextNeut.positie
    );
    let nextNeutWidth = nextNeut.customWidth || parseInt(neutDto.neutbreedte);
    let shouldCompensate = Math.abs((initialNextNeutPosition + nextNeutWidth) - this.canvasSize) < TRESHOLD;

    if (shouldCompensate) {
      return this.canvasSize - nextNeutWidth;
    }

    return initialNextNeutPosition;
  }

  public drawNeuten() {
    let currentDorpel: BestellingDorpelDto = this.dorpelData;
    let neuten: BestellingNeutDto[] = currentDorpel.neuten;
    let maatA = currentDorpel.maatA || 0;
    let maatZ = currentDorpel.maatZ || 0;
    let ctx: CanvasRenderingContext2D = this.canvasContext;
    let dorpelCode = this.dorpelData.dorpelCode;
    let options: NeutDrawingOptions = {
      isBuitenDorpel: dorpelCode.indexOf(DORPEL_CODES.BUITEN_DORPEL) !== -1,
      isBinnenDorpel: dorpelCode.indexOf(DORPEL_CODES.BINNEN_DORPEL) !== -1,
      isSlidingDoor: DorpelUtils.isSlidingDoor(dorpelCode),
    };
    let dorpelHeight = DorpelUtils.getThicknessFromDorpelCode(dorpelCode)

    let daagmaatVirtualRatio = this.calculateDaagmatVirtualRatio(neuten, maatA, maatZ, dorpelHeight);
    let scaledMA = Math.min(maatA, dorpelHeight) + Math.max(maatA - dorpelHeight, 0) * daagmaatVirtualRatio;
    let scaledMZ = Math.min(maatZ, dorpelHeight) + Math.max(maatZ - dorpelHeight, 0) * daagmaatVirtualRatio;

    if (maatA) {
      this.drawScaleMeasurements(ctx, 0, scaledMA, 30, SCALE_HEIGHT, maatA);
      this.drawTextLabel(
        ctx,
        0,
        scaledMA,
        DORPEL_START.y + dorpelHeight - 1,
        1,
        "Vak 1"
      );
    }

    let currentNeutPosition = scaledMA;

    neuten.forEach((neut, index) => {
      if (neut.neutCode) {
        const prevNeut: BestellingNeutDto = neuten[index - 1];
        if (prevNeut && prevNeut.sponningmaat) {
          options.leftCustomSponningmaat = prevNeut.sponningmaat
        } else {
          options.leftCustomSponningmaat = null
        }

        let neutRightLimit = this.drawNeut(
          neut,
          currentNeutPosition,
          DORPEL_START.y,
          ctx,
          options
        );
        let nextDaagmaat = neut.daagmaat;
        let nextNeutPosition =
          neutRightLimit + (nextDaagmaat ? nextDaagmaat * daagmaatVirtualRatio : 0);

        let willNextNeutTouchingRightEdge = index === neuten.length - 2 && !maatZ;

        if (willNextNeutTouchingRightEdge) {
          nextNeutPosition = this.compensateForRightEndAccuracy(nextNeutPosition, neuten[index + 1]);
        }

        if (nextDaagmaat) {
          this.drawScaleMeasurements(
            ctx,
            neutRightLimit,
            nextNeutPosition,
            30,
            SCALE_HEIGHT,
            nextDaagmaat
          );

          this.drawTextLabel(
            ctx,
            neutRightLimit,
            nextNeutPosition,
            DORPEL_START.y + dorpelHeight - 1,
            1,
            `Vak ${index + 2} Dagmaat`
          );

          if (neut.glaslat || neut.beglazingsprofiel || neut.aanslagprofiel) {
            let neutDto: NeutDto = this.uiService.getNeutDtoByCodeAndPositie(
              neut.neutCode,
              neut.positie
            );

            let nextNeut: NeutDto = (neuten[index + 1] as unknown) as NeutDto;
            let nextNeutDiff = 0;
            if (nextNeut) {
              let nextNeutDto: NeutDto = this.uiService.getNeutDtoByCodeAndPositie(nextNeut.neutCode!, nextNeut.positie!);
              nextNeutDiff = this.getNeutTopVsBaseWidthDifference(nextNeutDto, options, true);
            }
            let glaslatMarkVerticalPosition: number;

            if (options.isBuitenDorpel || (options.isBinnenDorpel && this.isConversionNeut(neutDto))) {
              glaslatMarkVerticalPosition =
                DORPEL_START.y +
                parseInt(neutDto.neutlengte) -
                SCALE_HEIGHT -
                20;
            }

            if (options.isBinnenDorpel || (options.isBuitenDorpel && this.isConversionNeut(neutDto))) {
              //TODO: if special neut we need to check if this.isConversionNeut() condition is respected for the nextNeutDto
              glaslatMarkVerticalPosition = DORPEL_START.y + SCALE_HEIGHT - 10;
            }

            let label = "";
            if (neut.glaslat && neut.beglazingsprofiel) {
              label = "Glaslat + Beglazingsprofiel"
            } else if (neut.glaslat) {
              label = "Glaslat"
            } else if (neut.beglazingsprofiel) {
              label = "Beglazingsprofiel"
            } else if (neut.aanslagprofiel) {
              label = "Aanslagprofiel"
            }

            this.drawScaleMeasurements(
              ctx,
              neutRightLimit - this.getNeutTopVsBaseWidthDifference(neutDto, options),
              nextNeutPosition + nextNeutDiff,
              glaslatMarkVerticalPosition,
              SCALE_HEIGHT,
              label,
              true
            );
          }
        }

        if (neut.sponningsoort) {
          this.markSpooningsoort(
            ctx,
            neut.sponningsoort,
            neutRightLimit,
            neutRightLimit + nextDaagmaat,
            DORPEL_START.y,
            dorpelHeight
          );
        }

        currentNeutPosition = nextNeutPosition;
      }
    });

    if (maatZ) {
      this.drawScaleMeasurements(
        ctx,
        currentNeutPosition,
        this.canvasSize,
        30,
        SCALE_HEIGHT,
        maatZ
      );
      this.drawTextLabel(
        ctx,
        currentNeutPosition,
        this.canvasSize,
        DORPEL_START.y + dorpelHeight - 1,
        1,
        `Vak ${neuten.length + 1}`
      );
    }
  }

  private getNeutTopVsBaseWidthDifference(neutDto: NeutDto, options: NeutDrawingOptions, isLeftSideDiffrence?: boolean): number {
    let startCoord, endCoord;

    if (neutDto.omschrijving?.indexOf("XL") !== -1 || neutDto.omschrijving?.indexOf("XT") !== -1) {
      startCoord = 1;
      endCoord = 2;
    } else if (neutDto.omschrijving?.indexOf("XR") !== -1) {
      startCoord = 0;
      endCoord = 3;
    } else if (NeutUtils.isDK(neutDto.omschrijving)) {
      if (NeutUtils.isDKLeft(neutDto.omschrijving)) {
        startCoord = 1;
        endCoord = 7;
      } else if (NeutUtils.isDKRight(neutDto.omschrijving)) {
        startCoord = 4;
        endCoord = 10;
      } else if (NeutUtils.isDKMidLeft(neutDto.omschrijving)) {
        startCoord = isLeftSideDiffrence ? 6 : 1;
        endCoord = isLeftSideDiffrence ? 11 : 3;
      }  else if (NeutUtils.isDKMidBoth(neutDto.omschrijving)) {
        startCoord = isLeftSideDiffrence ? 10 : 1;
        endCoord = isLeftSideDiffrence ? 16 : 7;
      } else if (NeutUtils.isDKMidRight(neutDto.omschrijving)) {
        startCoord = isLeftSideDiffrence ? 10 : 1;
        endCoord = isLeftSideDiffrence ? 12 : 7;
      }
    } else if (options.isBinnenDorpel || (options.isBuitenDorpel && (neutDto.omschrijving?.indexOf("W2") !== -1 || this.isConversionNeut(neutDto)))) {
      //W2 is not a conversion neut for buitendorpels on the right side w2 swithces from w1 e.g. which is a conversion neut back to regular
      if (neutDto.positie == "Links") {
        startCoord = 2;
        endCoord = 3;
      }

      if (neutDto.positie == "Rechts") {
        startCoord = 0;
        endCoord = 1;
      }

      if (neutDto.positie == "Tussen") {
        startCoord = isLeftSideDiffrence ? 0 : 4;
        endCoord = isLeftSideDiffrence ? 1 : 5;

        if (neutDto.omschrijving?.toLocaleLowerCase().indexOf("w3") !== -1) {
          startCoord = isLeftSideDiffrence ? 7 : 2;
          endCoord = isLeftSideDiffrence ? 6 : 3;
        } else if (neutDto.omschrijving?.toLocaleLowerCase().indexOf("w2") !== -1) {
          startCoord = isLeftSideDiffrence ? 0 : 5;
          endCoord = isLeftSideDiffrence ? 1 : 4;
        }
      }
    } else if (options.isBuitenDorpel || (options.isBinnenDorpel && this.isConversionNeut(neutDto))) {
      if (neutDto.positie == "Links") {
        startCoord = 3;
        endCoord = 2;
      }

      if (neutDto.positie == "Rechts") {
        startCoord = 5;
        endCoord = 4;
      }

      if (neutDto.positie == "Tussen") {
        startCoord = isLeftSideDiffrence ? 7 : 3;
        endCoord = isLeftSideDiffrence ? 6 : 2;
      }
    }

    let neutCoords = neutDto.tekening?.split("/");
    let start = parseInt(neutCoords![startCoord].split(",")[0]);
    let end = parseInt(neutCoords![endCoord].split(",")[0]);

    return end - start;
  }

  private isConversionNeut(neutDto: NeutDto): boolean {
    let isConversionNeut: boolean =
      neutDto.omschrijving?.indexOf("W1") !== -1 ||
      neutDto.omschrijving?.indexOf("W3") !== -1 ||
      neutDto.omschrijving?.indexOf("W4") !== -1 ||
      neutDto.omschrijving?.indexOf("W5") !== -1 || 
      NeutUtils.isDK(neutDto.omschrijving)

    return isConversionNeut;
  }

  private drawNeut(
    neut: BestellingNeutDto,
    startX: number,
    startY: number,
    ctx: CanvasRenderingContext2D,
    options: NeutDrawingOptions
  ): number {
    let neutDto: NeutDto = this.uiService.getNeutDtoByCodeAndPositie(
      neut.neutCode,
      neut.positie
    );
    let neutWidth = neut.customWidth || parseInt(neutDto.neutbreedte);
    let neutHeight = parseInt(neutDto.neutlengte);
    let leftEdge = startX;
    let rightEdge = startX + neutWidth;
    let isLeftNeut: boolean = neut.positie === "Links";
    let isRightNeut: boolean = neut.positie === "Rechts";
    let isMiddleNeut: boolean = neut.positie === "Tussen";
    let drawStartY: number = startY + CONTENT_OFFSET;
    let drawStartX: number = leftEdge;
    let shouldAlterTekeningString: boolean = false;
    let isDrawingFlipped = false;

    //wissel neuts convert a buiten dorpel into a binnen dorpel so they need to have the default orientation;
    let isConversionNeut: boolean =
      neutDto.omschrijving?.indexOf("W1") !== -1 ||
      neutDto.omschrijving?.indexOf("W4") !== -1 ||
      neutDto.omschrijving?.indexOf("W5") !== -1;
    let tekeningString: string = neutDto.tekening;

    if (neutDto.hasTekeningStringException) {
      this.flipDraw(neutHeight);
      isDrawingFlipped = true;
    }

    if (neut.customWidth && NeutUtils.isSpecial(neutDto.omschrijving)) {
      tekeningString = this.updateTekeningStringWithCustomWidth(tekeningString, neut.customWidth);
    }

    if (neutDto.isInHouseNeut) {
      // condition used to be !this.uiService.fromPrefilledOrder
      if (isLeftNeut) {
        drawStartX = leftEdge + CONTENT_OFFSET;
        shouldAlterTekeningString =
          this.uiService.kloosterSponningOptions.isLeftSide ||
          this.uiService.renovatieSponningOptions.isLeftSide;
      } else if (isRightNeut) {
        drawStartX = leftEdge - CONTENT_OFFSET;
        shouldAlterTekeningString =
          this.uiService.kloosterSponningOptions.isRightSide ||
          this.uiService.renovatieSponningOptions.isRightSide;
      }

      if (options.isBuitenDorpel && (neut.sponningmaat || options.leftCustomSponningmaat)) {
        //only wissels have sponningmaat
        //sponningmaat is only populated on the model if different from default (default is already included in tekening)
        tekeningString = this.changeTekeningStringSponningmaatValue(
          tekeningString,
          neut.neutCode,
          neut.sponningmaat,
          options.leftCustomSponningmaat
        );
      }

      let isW2Neut = neutDto.omschrijving.indexOf("W2") !== -1;
      let isW3Neut = neutDto.omschrijving.indexOf("W3") !== -1;
      tekeningString = this.insertVoorsponningDeviation({
        linksVoorsponning: neut.linksVoorsponning,
        rechtsVoorsponning: neut.rechtsVoorsponning,
        tekeningString: tekeningString,
        isTopPointingNeut:
          options.isBinnenDorpel ||
          (options.isBuitenDorpel && isConversionNeut) ||
          NeutUtils.isDK(neutDto.omschrijving),
        isW2Neut: isW2Neut, //voorsponning exception (bidirectional neut)
        isW3Neut: isW3Neut, //voorsponning exception (bidirectional neut)
        isLeftNeut: isLeftNeut,
        isRightNeut: isRightNeut,
        isMiddleNeut: isMiddleNeut,
        neutHeight: neutHeight,
      });
    }

    if (shouldAlterTekeningString) {
      let isCutDirectionRight = isLeftNeut;
      tekeningString = this.insertBewerkingenDeviation({
        isCutDirectionRight: isCutDirectionRight,
        isLeftNeut: isLeftNeut,
        neutWidth: neutWidth,
        neutHeight: neutHeight,
        tekeningString: tekeningString,
        isKloosterSponning: this.uiService.kloosterSponningOptions?.isActive && ((isLeftNeut && this.uiService.kloosterSponningOptions?.isLeftSide) || (isRightNeut && this.uiService.kloosterSponningOptions?.isRightSide)),
        isRenovatieSponning: this.uiService.renovatieSponningOptions?.isActive && ((isLeftNeut && this.uiService.renovatieSponningOptions?.isLeftSide) || (isRightNeut && this.uiService.renovatieSponningOptions?.isRightSide)),
        isDrawingFlipped: isDrawingFlipped,
      });
    }

    let neutCoordinatePoints = NeutUtils.getDrawingCoorodatesList(
      tekeningString,
      neutWidth,
      drawStartX,
      drawStartY
    );
    this.drawByCoordinates(
      ctx,
      neutCoordinatePoints,
      neut.isInHouseNeut ? NEUT_COLOR : INVALID_NEUT_COLOR
    );

    if (neutDto.hasTekeningStringException) {
      this.resetFlip();
      isDrawingFlipped = false;
    }

    this.drawScaleMeasurements(
      ctx,
      leftEdge,
      rightEdge,
      30,
      SCALE_HEIGHT,
      neutWidth
    );
    return rightEdge;
  }

  private updateTekeningStringWithCustomWidth(tekeningString: string, customWidth: number): string {
    let tekeningCoordinates: Coordinate[] =
      this.convertTekeningStringToCoordinatesList(tekeningString);
    tekeningCoordinates[1].x = customWidth;
    tekeningCoordinates[2].x = customWidth;
    return this.convertCoordinatesListToTekeningString(tekeningCoordinates);
  }

  private changeTekeningStringSponningmaatValue(
    tekeningString: string,
    neutCode: string,
    rightSponningmaat: number,
    leftSponningmaat: number
  ): string {
    let wisselTypeToken: string = neutCode.split(".")[1].substr(0, 2);
    let targetPoints: SponningmaatUpdateTargetPoints =
      this.getSponningmaatUpdateTargetPoints(wisselTypeToken);
    let tekeningCoordinates: Coordinate[] =
      this.convertTekeningStringToCoordinatesList(tekeningString);

    targetPoints?.right.forEach((tp) => {
      if (rightSponningmaat) {
        tekeningCoordinates[tp].y = rightSponningmaat;
      }
    });

    targetPoints?.left.forEach((tp) => {
      if (leftSponningmaat) {
        tekeningCoordinates[tp].y = leftSponningmaat;
      }
    });

    return this.convertCoordinatesListToTekeningString(tekeningCoordinates);
  }

  private getSponningmaatUpdateTargetPoints(wisselType: string): SponningmaatUpdateTargetPoints {
    return WISSELNEUT_SPONNINGMAAT_COORDINATES[wisselType];
  }

  private drawByCoordinates(
    ctx: CanvasRenderingContext2D,
    coordinates: any[],
    fillColor?: string
  ): void {
    let startPoint = coordinates[0];

    ctx.beginPath();
    ctx.moveTo(startPoint.x, startPoint.y);
    coordinates.forEach((coordinate, index) => {
      if (index) {
        ctx.lineTo(coordinate.x, coordinate.y);
        ctx.stroke();
      }
    });

    if (fillColor) {
      this.fillShape(ctx, fillColor);
    }
  }

  public markSpooningsoort(
    ctx: CanvasRenderingContext2D,
    sponningsoort: string,
    fromX: number,
    toX: number,
    dorpelTopY: number,
    dorpelHeight: number
  ) {
    let extractedLetter = sponningsoort.charAt(0);
    ctx.fillText(
      extractedLetter,
      fromX + (toX - fromX) / 2,
      dorpelTopY + dorpelHeight - 5
    );
  }

  public fillShape(ctx: CanvasRenderingContext2D, color: string) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
  }

  /**
   * SLUITPOT DRAWING FUNCTIONS
   */

  public drawSluitpotten(damLevel?: number) {
    let sluitpotten: SluitpotModel[] = this.dorpelData.sluitpotten;
    let ctx: CanvasRenderingContext2D = this.canvasContext;
    let minimumScaleY = 185;
    let scaleSpacing = SCALE_HEIGHT + 10;

    sluitpotten.forEach((sluitpot, index) => {
      if (!sluitpot.maat /*|| (sluitpot.isNonBlinde && !sluitpot.maat2 && sluitpot.maat2 !== 0)*/) {
        //if no distance is mentioned (or is 0), avoid drawing the sluitpot
        //we can draw sluitpotten with height 0 (maat2 == 0), though
        return;
      }

      let ratio = this.canvasSize / this.dorpelData.zaagmaat;
      this.drawSluitpott(ctx, ratio, sluitpot, damLevel);
      this.drawScaleMeasurements(
        ctx,
        0,
        sluitpot.maat * ratio,
        minimumScaleY + index * scaleSpacing,
        SCALE_HEIGHT,
        sluitpot.maat
      );
    });
  }

  private drawSluitpott(
    ctx: CanvasRenderingContext2D,
    ratio: number,
    sluitpot: SluitpotModel,
    damLevel?: number
  ): void {
    let sluitpotMarkerSize = 10;
    let markerLevels = {
      dam: 90,
      sponning: 140,
    };
    let sluitpotValue = sluitpot.maat * ratio;

    if (!sluitpotValue) {
      return;
    }

    let yOffset = 0;

    if (/*sluitpot.isNonBlinde*/ false) {
      yOffset += DORPEL_START.y + (damLevel || 0) + (sluitpot.maat2 || 0);
    } else {
      if (SluitpotUtils.isInDeDam(sluitpot.omschrijving)) {
        yOffset = markerLevels.dam;
      } else if (SluitpotUtils.isInDeSponning(sluitpot.omschrijving)) {
        yOffset = markerLevels.sponning;
      }
    }

    ctx.beginPath();
    ctx.moveTo(sluitpotValue - sluitpotMarkerSize / 2, yOffset - sluitpotMarkerSize / 2);
    ctx.lineTo(
      sluitpotValue + sluitpotMarkerSize / 2,
      yOffset + sluitpotMarkerSize / 2
    );
    ctx.moveTo(sluitpotValue + sluitpotMarkerSize / 2, yOffset - sluitpotMarkerSize / 2);
    ctx.lineTo(
      sluitpotValue - sluitpotMarkerSize / 2,
      yOffset + sluitpotMarkerSize / 2
    );
    ctx.stroke();
  }

  /**
   * SCALE related functions
   */

  private drawScaleMeasurements(
    ctx: CanvasRenderingContext2D,
    fromX: number,
    toX: number,
    verticalPosition: number,
    height: number,
    value?: number | string,
    excludeSideDelimiters: boolean = false
  ) {
    let midY = (2 * verticalPosition + height) / 2;

    if (!excludeSideDelimiters) {
      ctx.beginPath();
      ctx.moveTo(fromX, verticalPosition);
      ctx.lineTo(fromX, verticalPosition + height);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(toX, verticalPosition);
      ctx.lineTo(toX, verticalPosition + height);
      ctx.stroke();
    }

    this.drawArrowHeadLeft(
      ctx,
      { x: fromX, y: midY },
      SCALE_TIP_ARROW.HEIGHT,
      SCALE_TIP_ARROW.WIDTH
    );
    this.drawArrowHeadRight(
      ctx,
      { x: toX, y: midY },
      SCALE_TIP_ARROW.HEIGHT,
      SCALE_TIP_ARROW.WIDTH
    );

    ctx.beginPath();
    ctx.moveTo(fromX, midY);
    ctx.lineTo(toX, midY);
    ctx.stroke();
    ctx.lineWidth = TEXT_STROKE_WIDTH;

    if (value) {
      this.drawTextLabel(ctx, fromX, toX, verticalPosition, height, value);
    }

    ctx.lineWidth = DOOR_ELEMENT_WIDTH;
  }

  public drawTextLabel(ctx: CanvasRenderingContext2D,
    fromX: number,
    toX: number,
    verticalPosition: number,
    height: number,
    value: number | string) {
    let midY = (2 * verticalPosition + height) / 2;
    let cachedFont = ctx.font;
    let text = value.toString();
    ctx.font = SCALE_TEXT_FONT;
    let textSize = ctx.measureText(text);
    ctx.fillText(text, fromX + (toX - fromX) / 2 - textSize.width / 2, midY - 5);
    ctx.font = cachedFont;
  }

  private drawArrowHeadLeft(
    ctx: CanvasRenderingContext2D,
    tipPoint: any,
    height: number,
    width: number
  ) {
    ctx.beginPath();
    ctx.moveTo(tipPoint.x, tipPoint.y);
    ctx.lineTo(tipPoint.x + height, tipPoint.y + width / 2);
    ctx.lineTo(tipPoint.x + height, tipPoint.y - width / 2);
    ctx.fill();
  }

  private drawArrowHeadRight(
    ctx: CanvasRenderingContext2D,
    tipPoint: any,
    height: number,
    width: number
  ) {
    ctx.beginPath();
    ctx.moveTo(tipPoint.x, tipPoint.y);
    ctx.lineTo(tipPoint.x - height, tipPoint.y + width / 2);
    ctx.lineTo(tipPoint.x - height, tipPoint.y - width / 2);
    ctx.fill();
  }

  /**
   * SLIDING DOOR UTILS
   */

  private getSlidingNeutValues(doorThickness: number, neutLength: number, dorpelCode): any {
    let result = {
      a1: null,
      a2: null,
      b: 6,
      c: 42,
      baseline: 60,
    };

    if (doorThickness == 140) {
      if (neutLength == 114 || neutLength == 122) {
        result.a1 = 6;
        result.a2 = 6;
      } else if (neutLength == 139) {
        result.a1 = 6;
        result.a2 = 12;
      } else {
        result.a1 = 6;
        result.a2 = 6;
      }
    } else if (doorThickness == 146) {
      if (neutLength == 146) {
        result.a1 = 12;
        result.a2 = 12;
      } else if (neutLength == 139) {
        result.a1 = 12;
        result.a2 = 6;
      } else {
        result.a1 = 6;
        result.a2 = 6;
      }
    }

    let hasSpecialNeutException = dorpelCode.toLocaleLowerCase().indexOf("hs.190") !== -1;
    if (hasSpecialNeutException) {
      result.b = 0;
    }

    return result;
  }

  /**
   * GENERAL UTILS
   */

  private convertTekeningStringToCoordinatesList(
    tekeningString: string
  ): Coordinate[] {
    let splitTekening = tekeningString.split("/");
    let response: Coordinate[] = [];

    splitTekening.forEach((tekeningPoint) => {
      response.push({
        x: parseInt(tekeningPoint.split(",")[0]),
        y: parseInt(tekeningPoint.split(",")[1]),
      });
    });

    return response;
  }

  private convertCoordinatesListToTekeningString(
    coordinatesList: Coordinate[]
  ): string {
    let stringifiedCooridinatesList: string[] = [];
    coordinatesList.forEach((coordinate) => {
      stringifiedCooridinatesList.push(`${coordinate.x},${coordinate.y}`);
    });
    return stringifiedCooridinatesList.join("/");
  }

  /**
   * VOORSPONNING DEVIATION
   *
   */

  private insertVoorsponningDeviation(options: any): string {
    let tekeningCoordinatesList: Coordinate[] =
      this.convertTekeningStringToCoordinatesList(options.tekeningString);
    let linksVoorsponning = options.linksVoorsponning;
    let rechtsVoorsponning = options.rechtsVoorsponning;
    let insertionPointDeterminingOptions = {
      isLeftNeut: options.isLeftNeut,
      isRightNeut: options.isRightNeut,
      isMiddleNeut: options.isMiddleNeut,
      isTopPointingNeut: options.isTopPointingNeut,
      indices: null,
      voorsponningType: null,
    };

    if (linksVoorsponning && linksVoorsponning.width) {
      let isVoorsponningOnTop = options.isTopPointingNeut || options.isW2Neut; //w2 neut types have left voorsponning on the top side (right on the bottom)
      let neutTopPositionIndices = this.getNeutTopEdgePointsIndices(
        tekeningCoordinatesList,
        {
          topSideYValue: isVoorsponningOnTop ? 0 : options.neutHeight, //the discriminant for the top edge of the neut
        }
      );
      insertionPointDeterminingOptions.indices = neutTopPositionIndices;
      insertionPointDeterminingOptions.isTopPointingNeut = isVoorsponningOnTop; //override initial value with additional check;
      insertionPointDeterminingOptions.voorsponningType =
        VOORSPONNING_TYPES.LEFT;

      let insertionPointIndex: number =
        this.determineVoorsponningInsertionPoint(
          insertionPointDeterminingOptions
        );
      let targetPoint: Coordinate =
        tekeningCoordinatesList[insertionPointIndex];
      let deviation: Coordinate[] = this.getLinksVoorsponningDeviation(
        isVoorsponningOnTop,
        targetPoint,
        linksVoorsponning
      );
      tekeningCoordinatesList.splice(insertionPointIndex, 1, ...deviation);

      if (insertionPointIndex === 0) {
        //exception for when tekening string starts exactly at the insertion point for left deviation (we also need to update last coordinate)
        //this case was encountered for DK neuten where the starting point of the coordinate list is an exception (not the leftmost + topmost but topmost + leftmost)
        tekeningCoordinatesList[tekeningCoordinatesList.length - 1] = tekeningCoordinatesList[0];
      }
    }

    if (rechtsVoorsponning && rechtsVoorsponning.width) {
      let isVoorsponningOnTop = options.isTopPointingNeut || options.isW3Neut; //w3 neut types have right voorsponning on the top side (left on the bottom)
      let neutTopPositionIndices = this.getNeutTopEdgePointsIndices(
        tekeningCoordinatesList,
        {
          topSideYValue: isVoorsponningOnTop ? 0 : options.neutHeight, //the discriminant for the top edge of the neut
        }
      );
      insertionPointDeterminingOptions.indices = neutTopPositionIndices;
      insertionPointDeterminingOptions.isTopPointingNeut = isVoorsponningOnTop; //override initial value with additional check;
      insertionPointDeterminingOptions.voorsponningType =
        VOORSPONNING_TYPES.RIGHT;

      let insertionPointIndex: number =
        this.determineVoorsponningInsertionPoint(
          insertionPointDeterminingOptions
        );
      let targetPoint: Coordinate =
        tekeningCoordinatesList[insertionPointIndex];
      let deviation: Coordinate[] = this.getRechtsVoorsponningDeviation(
        options.isTopPointingNeut || options.isW3Neut,
        targetPoint,
        rechtsVoorsponning
      );
      tekeningCoordinatesList.splice(insertionPointIndex, 1, ...deviation);
    }

    return this.convertCoordinatesListToTekeningString(tekeningCoordinatesList);
  }

  private getNeutTopEdgePointsIndices(
    tekeningCoordinatesList: Coordinate[],
    options: any
  ): any {
    // the point indices order reflects the tekening string traversal order (drawing order)
    let firstPointIndex;
    let secondPointIndex;

    for (let i = 0; i < tekeningCoordinatesList.length; i++) {
      if (tekeningCoordinatesList[i].y === options.topSideYValue) {
        if (typeof firstPointIndex === "undefined") {
          firstPointIndex = i;
        } else if (typeof secondPointIndex === "undefined") {
          secondPointIndex = i;
        }
      }
    }

    return {
      firstPointIndex: firstPointIndex,
      secondPointIndex: secondPointIndex,
    };
  }

  private determineVoorsponningInsertionPoint(options: any): number {
    if (options.isLeftNeut) {
      return options.isTopPointingNeut
        ? options.indices.secondPointIndex
        : options.indices.firstPointIndex;
    } else if (options.isRightNeut) {
      return options.isTopPointingNeut
        ? options.indices.firstPointIndex
        : options.indices.secondPointIndex;
    } else if (options.isMiddleNeut) {
      if (options.voorsponningType === VOORSPONNING_TYPES.LEFT) {
        return options.isTopPointingNeut
          ? options.indices.firstPointIndex
          : options.indices.secondPointIndex;
      } else if (options.voorsponningType === VOORSPONNING_TYPES.RIGHT) {
        return options.isTopPointingNeut
          ? options.indices.secondPointIndex
          : options.indices.firstPointIndex;
      }
    }
  }

  private getLinksVoorsponningDeviation(
    isTopPointingNeut: boolean,
    targetPoint: Coordinate,
    voorsponning: VorsponningSize
  ): Coordinate[] {
    let vorsponningHeight = voorsponning.height || voorsponning.width;
    return isTopPointingNeut
      ? this.convertTekeningStringToCoordinatesList(
        `${targetPoint.x},${targetPoint.y + vorsponningHeight}/${targetPoint.x + voorsponning.width
        },${targetPoint.y + vorsponningHeight}/${targetPoint.x + voorsponning.width
        },${targetPoint.y}`
      )
      : this.convertTekeningStringToCoordinatesList(
        `${targetPoint.x + voorsponning.width},${targetPoint.y}/${targetPoint.x + voorsponning.width
        },${targetPoint.y - vorsponningHeight}/${targetPoint.x},${targetPoint.y - vorsponningHeight
        }`
      );
  }

  private getRechtsVoorsponningDeviation(
    isTopPointingNeut: boolean,
    targetPoint: Coordinate,
    voorsponning: VorsponningSize
  ): Coordinate[] {
    let vorsponningHeight = voorsponning.height || voorsponning.width;
    return isTopPointingNeut
      ? this.convertTekeningStringToCoordinatesList(
        `${targetPoint.x - voorsponning.width},${targetPoint.y}/${targetPoint.x - voorsponning.width
        },${targetPoint.y + vorsponningHeight}/${targetPoint.x},${targetPoint.y + vorsponningHeight
        }`
      )
      : this.convertTekeningStringToCoordinatesList(
        `${targetPoint.x},${targetPoint.y - vorsponningHeight}/${targetPoint.x - voorsponning.width
        },${targetPoint.y - vorsponningHeight}/${targetPoint.x - voorsponning.width
        },${targetPoint.y}`
      );
  }

  /**
   * BEWERKINGEN UTILS
   */

  private insertBewerkingenDeviation(options: any): string {
    let tekeningCoordinatesList: Coordinate[] =
      this.convertTekeningStringToCoordinatesList(options.tekeningString);
    let targetPoint: Coordinate = {
      x: options.isLeftNeut ? 0 : options.neutWidth,
      y: 0,
    };

    if (options.isDrawingFlipped) {
      //change target point to bottom omologue
      targetPoint.y = options.neutHeight;
    }

    let insertionPointIndex: number = this.determineBewerkingInsertionPoint(
      tekeningCoordinatesList,
      targetPoint
    );

    if (options.isKloosterSponning) {
      let deviation: string = this.getKloosterDeviation({
        targetPoint: targetPoint,
        isLeftNeut: options.isLeftNeut,
        neutHeight: options.neutHeight,
      });
      let deviationCoordinates: Coordinate[] =
        this.convertTekeningStringToCoordinatesList(deviation);

      if (options.isLeftNeut) {
        deviationCoordinates.reverse().push(targetPoint);
      } else {
        deviationCoordinates.unshift(targetPoint);
      }

      tekeningCoordinatesList.splice(
        insertionPointIndex,
        1,
        ...deviationCoordinates
      );
    }

    if (options.isRenovatieSponning) {
      let deviation: string = this.getRenovatieDeviation({
        targetPoint: targetPoint,
        isLeftNeut: options.isLeftNeut,
        isDrawingFlipped: options.isDrawingFlipped,
      });
      let deviationCoordinates: Coordinate[] =
        this.convertTekeningStringToCoordinatesList(deviation);
      tekeningCoordinatesList.splice(
        insertionPointIndex,
        1,
        ...deviationCoordinates
      );
    }

    if (options.isLeftNeut) {
      tekeningCoordinatesList[tekeningCoordinatesList.length - 1] =
        tekeningCoordinatesList[0];
    }

    return this.convertCoordinatesListToTekeningString(tekeningCoordinatesList);
  }

  private determineBewerkingInsertionPoint(
    coordinatesList: Coordinate[],
    targetPoint: Coordinate
  ): number | null {
    let foundIndex = null;

    for (let i = 0; i < coordinatesList.length; i++) {
      if (
        coordinatesList[i].x === targetPoint.x &&
        coordinatesList[i].y === targetPoint.y
      ) {
        foundIndex = i;
        break;
      }
    }

    return foundIndex;
  }

  private getRenovatieDeviation(options: any): string {
    let renovatieSponningData = this.getRenovatieSponningData();
    let targetPoint = options.targetPoint;

    if (options.isDrawingFlipped) {
      //we calculate different deviation for neut flipping (only for asymetric deviations - renovatie)
      if (options.isLeftNeut) {
        return `${targetPoint.x + renovatieSponningData.renovatieDepthLeft},${targetPoint.y
          }/
                    ${targetPoint.x + renovatieSponningData.renovatieDepthLeft},${targetPoint.y - renovatieSponningData.renovatieLengthLeft
          }/
                    ${targetPoint.x},${targetPoint.y - renovatieSponningData.renovatieLengthLeft
          }`;
      } else {
        return `${targetPoint.x},${targetPoint.y - renovatieSponningData.renovatieLengthRight
          }/
                    ${targetPoint.x - renovatieSponningData.renovatieDepthRight},${targetPoint.y - renovatieSponningData.renovatieLengthRight
          }/
                    ${targetPoint.x - renovatieSponningData.renovatieDepthRight},${targetPoint.y
          }`;
      }
    }

    if (options.isLeftNeut) {
      return `${targetPoint.x},${targetPoint.y + renovatieSponningData.renovatieLengthLeft
        }/
                ${targetPoint.x + renovatieSponningData.renovatieDepthLeft},${targetPoint.y + renovatieSponningData.renovatieLengthLeft
        }/
                ${targetPoint.x + renovatieSponningData.renovatieDepthLeft},${targetPoint.y
        }`;
    } else {
      return `${targetPoint.x - renovatieSponningData.renovatieDepthRight},${targetPoint.y
        }/
                ${targetPoint.x - renovatieSponningData.renovatieDepthRight},${targetPoint.y + renovatieSponningData.renovatieLengthRight
        }/
                ${targetPoint.x},${targetPoint.y + renovatieSponningData.renovatieLengthRight
        }`;
    }
  }

  private getKloosterDeviation(options: any): string {
    let kloosterSponningData = this.getKloosterSponningData();
    let targetPoint = options.targetPoint;
    // let totalHeight = options.neutHeight;
    let kloosterTopBottomOffset = options.isLeftNeut ? kloosterSponningData.kloosterOffsetLeft : kloosterSponningData.kloosterOffsetRight;

    if (options.isLeftNeut) {
      return `${targetPoint.x},${targetPoint.y + kloosterTopBottomOffset}/
                ${targetPoint.x + kloosterSponningData.kloosterDepthLeft},${targetPoint.y + kloosterTopBottomOffset
        }/
                ${targetPoint.x + kloosterSponningData.kloosterDepthLeft},${targetPoint.y +
        kloosterTopBottomOffset +
        kloosterSponningData.kloosterLengthLeft
        }/
                ${targetPoint.x},${targetPoint.y +
        kloosterTopBottomOffset +
        kloosterSponningData.kloosterLengthLeft
        }`;
    } else {
      return `${targetPoint.x},${targetPoint.y + kloosterTopBottomOffset}/
                ${targetPoint.x - kloosterSponningData.kloosterDepthRight},${targetPoint.y + kloosterTopBottomOffset
        }/
                ${targetPoint.x - kloosterSponningData.kloosterDepthRight},${targetPoint.y +
        kloosterTopBottomOffset +
        kloosterSponningData.kloosterLengthRight
        }/
                ${targetPoint.x},${targetPoint.y +
        kloosterTopBottomOffset +
        kloosterSponningData.kloosterLengthRight
        }`;
    }
  }

  private getKloosterSponningData() {
    //TODO: move in bewerkingen comp
    return {
      kloosterDepthLeft: this.uiService.kloosterSponningOptions.leftCustomDepth,
      kloosterDepthRight: this.uiService.kloosterSponningOptions.rightCustomDepth,
      kloosterLengthLeft: this.uiService.kloosterSponningOptions.leftCustomLength,
      kloosterLengthRight: this.uiService.kloosterSponningOptions.rightCustomLength,
      kloosterOffsetLeft: this.uiService.kloosterSponningOptions.leftCustomOffset,
      kloosterOffsetRight: this.uiService.kloosterSponningOptions.rightCustomOffset,
    };
  }

  private getRenovatieSponningData() {
    //TODO: move in bewerkingen comp
    return {
      renovatieDepthLeft: this.uiService.renovatieSponningOptions.leftCustomDepth,
      renovatieDepthRight: this.uiService.renovatieSponningOptions.rightCustomDepth,
      renovatieLengthLeft: this.uiService.renovatieSponningOptions.leftCustomLength,
      renovatieLengthRight: this.uiService.renovatieSponningOptions.rightCustomLength,
    };
  }

  public calculateVerstekLengthByAngle(dorpelHeight: number, angle?: number): number {
    //maybe extract dorpelwidth of model (now width is static)
    let verstekAngle = angle || 45; //TODO: extract off the uistate when we have fields and pass it in on callsite
    let tangentValueOfAngle = Math.tan((verstekAngle * Math.PI) / 180);

    return Math.round(dorpelHeight * tangentValueOfAngle);
  }

  private setCanvasSize(
    canvas: HTMLCanvasElement,
    width: number,
    height: number
  ) {
    let ratio = window.devicePixelRatio;
    let style = canvas.style;

    style.width = "" + width + "px";
    style.height = "" + height + "px";

    canvas.width = width;
    canvas.height = height;
  }


  private flipDraw(referenceHeight: number): void {
    let ctx = this.canvasContext;
    ctx.save();
    ctx.translate(0, 2 * (DORPEL_START.y + CONTENT_OFFSET) + referenceHeight);
    ctx.scale(1, -1);
  }

  private resetFlip(): void {
    let ctx = this.canvasContext;
    ctx.restore();
  }
}
